(ns pfo.im.component
  (:require [reagent.core :as reagent :refer [atom]]
            [promesa.core :as p]
            [clojure.string :as str]
            [pfo.api :as api]
            [pfo.notify :as notify]
            [pfo.im.model :as model]
            [pfo.collection.model :as collection-model]
            [pfo.controller :as controller]))


(def ^:private initial-state
  {:conversations        []
   :current-conv-id      nil
   :messages             {}
   :input-content        {}
   :loading-messages?    false
   :sending?             false
   :error                nil
   :has-more-messages?   {}
   :delivery-status      {}
   :retrying             {}
   :typing               {}
   :families             []
   :users-by-family      {}
   :users-loaded?        false
   :new-conv-search      ""
   :new-conv-family      nil
   :family-filter        nil
   :loading-users?       false
   :panel-open?          false
   :panel-view           :list
   :active-tab           :all
   :search-query         ""})

(defonce im-state (atom initial-state))

(defn reset-state! []
  (reset! im-state initial-state))

(defonce ^:private handlers-registered? (atom false))

(defn- current-user-id []
  (api/get-auth-info :user-id))

(defn- swap-state! [& args]
  (apply swap! im-state args))

(defonce ^:private typing-timers (atom {}))

;; ---------------------------------------------------------------------------
;; Truncated Exponential Backoff — automatic message send retry
;; ---------------------------------------------------------------------------

(defonce ^:private retry-timers (atom {}))

(def ^:private max-retries 5)

(defn- backoff-delay-ms [attempt]
  ;; attempt 0→1s, 1→2s, 2→4s, 3→8s, 4→16s (cap)
  (min 16000 (+ (* 1000 (.pow js/Math 2 attempt)) (rand-int 300))))

(defn- cancel-retry! [temp-id]
  (when-let [timer-id (get @retry-timers temp-id)]
    (js/clearTimeout timer-id))
  (swap! retry-timers dissoc temp-id)
  (swap-state! update :retrying dissoc temp-id))

(defn- cancel-all-retries! []
  (doseq [[_ timer-id] @retry-timers]
    (js/clearTimeout timer-id))
  (reset! retry-timers {})
  (swap-state! assoc :retrying {}))

(defn- mark-message-failed!
  ([conv-id temp-id] (mark-message-failed! conv-id temp-id nil))
  ([conv-id temp-id error-msg]
   (cancel-retry! temp-id)
   (swap-state! (fn [s]
                  (-> s
                      (assoc :sending? false)
                      (update-in [:messages conv-id]
                                 (fn [msgs]
                                   (mapv #(if (= (:id %) temp-id)
                                            (cond-> (assoc % :status :failed)
                                              error-msg (assoc :error-msg error-msg))
                                            %)
                                         msgs))))))))

;; The picker's last-activity label and the list preview fall back to
;; :last_message from the cached conversation list — keep it in sync as
;; messages are sent and received, so reopening the picker doesn't show
;; "No chat yet" for a thread with fresh activity. Takes the raw server
;; message (the same shape GET /im/conversations embeds).
(defn- set-conv-last-message! [conv-id msg]
  (swap-state! update :conversations
               (fn [convs]
                 (mapv #(if (= (:id %) conv-id) (assoc % :last_message msg) %) convs))))

(defn- unrecoverable-send-error? [e]
  (and (= 400 (:status e))
       (get-in e [:response :body :message])))

(defn- schedule-retry! [conv-id temp-id content attempt start-ms]
  (let [elapsed   (- (.now js/Date) start-ms)
        remaining (- 60000 elapsed)]
    (if (or (<= remaining 500) (>= attempt max-retries))
      (mark-message-failed! conv-id temp-id)
      (let [delay    (min remaining (backoff-delay-ms attempt))
            timer-id (js/setTimeout
                      (fn []
                        (swap! retry-timers dissoc temp-id)
                        (let [current (some #(when (= (:id %) temp-id) %)
                                            (get-in @im-state [:messages conv-id]))]
                          (when (= :pending (:status current))
                            (controller/dispatch :im-send-message :send
                              {:conv-id    conv-id
                               :content    content
                               :on-success (fn [msg]
                                             (let [ui-msg  (model/message->ui msg)
                                                   real-id (:id ui-msg)]
                                               (set-conv-last-message! conv-id msg)
                                               (swap-state! (fn [s]
                                                              (-> s
                                                                  (update-in [:messages conv-id]
                                                                             (fn [msgs]
                                                                               (let [already? (some #(= (:id %) real-id) msgs)]
                                                                                 (if already?
                                                                                   (vec (remove #(= (:id %) temp-id) msgs))
                                                                                   (mapv #(if (= (:id %) temp-id) ui-msg %) msgs)))))
                                                                  (update :delivery-status #(if (get % real-id) % (assoc % real-id model/status-sent)))
                                                                  (update :retrying dissoc temp-id))))))
                               :on-error   (fn [e]
                                             (if (unrecoverable-send-error? e)
                                               (mark-message-failed! conv-id temp-id (get-in e [:response :body :message]))
                                               (schedule-retry! conv-id temp-id content (inc attempt) start-ms)))}))))
                      delay)]
        (swap! retry-timers assoc temp-id timer-id)
        (swap-state! update :retrying assoc temp-id {:count attempt :start-ms start-ms})))))

(defn- send-typing! [conv-id]
  (when-let [t (get @typing-timers conv-id)]
    (js/clearTimeout t))
  (swap! typing-timers assoc conv-id
         (js/setTimeout #(swap! typing-timers dissoc conv-id) 2000))
  (controller/dispatch :im-typing :send {:conv-id conv-id}))

(defn- clear-typing! [conv-id user-id]
  (swap-state! update-in [:typing conv-id] dissoc user-id))

(defn- conv-messages [conv-id]
  (get-in @im-state [:messages conv-id] []))

(defn- current-input []
  (get-in @im-state [:input-content (:current-conv-id @im-state)] ""))

(defn- conversation-by-id [conv-id]
  (->> (:conversations @im-state)
       (filter #(= (:id %) conv-id))
       first))

(defn- total-unread []
  (->> (:conversations @im-state)
       (map #(:unread_count % 0))
       (reduce + 0)))

(defn- mfo?
  "Multi-family office context: the user has access to more than one family.
  With a single family the pre-MFO chat UI stays unchanged."
  []
  (> (count (:families @im-state)) 1))

(declare ensure-users-loaded!)


(defn- load-conversations! []
  (controller/dispatch :im-conversations :load
    {:on-success (fn [convs] (swap-state! assoc :conversations (or convs [])))
     :on-error   (fn [_] (swap-state! assoc :error "Failed to load conversations"))}))

(defn- load-messages! [conv-id & [before-id]]
  (swap-state! assoc :loading-messages? true)
  (controller/dispatch :im-get-messages :load
    {:conv-id   conv-id
     :before-id before-id
     :on-success (fn [msgs]
                   (let [msgs    (or msgs [])
                         ui-msgs (vec (sort-by :created-at (mapv model/message->ui msgs)))]
                     (if before-id
                       (swap-state! update-in [:messages conv-id] #(vec (concat ui-msgs %)))
                       (swap-state! assoc-in [:messages conv-id] ui-msgs))
                     (swap-state! update :delivery-status
                                  (fn [ds]
                                    (reduce (fn [acc msg]
                                              (if-let [status (not-empty (:status msg))]
                                                (assoc acc (:id msg) status)
                                                acc))
                                            ds msgs)))
                     (swap-state! assoc-in [:has-more-messages? conv-id] (= (count msgs) 50))
                     (swap-state! assoc :loading-messages? false)))
     :on-error   (fn [_]
                   (swap-state! assoc :loading-messages? false
                                :error "Failed to load messages"))}))

(defn- select-conversation! [conv-id]
  (swap-state! assoc :current-conv-id conv-id
               :panel-view :conversation)
  ;; The MFO banner needs per-family users to offer the common-family toggle.
  (when (mfo?)
    (ensure-users-loaded!))
  (swap-state! update :conversations
               (fn [convs]
                 (mapv #(if (= (:id %) conv-id) (assoc % :unread_count 0) %) convs)))
  (load-messages! conv-id)
  (controller/dispatch :im-mark-read :mark {:conv-id conv-id}))

(defn- retry-message! [conv-id msg]
  (let [temp-id  (:id msg)
        content  (:content msg)
        current  (some #(when (= (:id %) temp-id) %) (get-in @im-state [:messages conv-id]))]
    (when (= :failed (:status current))
      (swap-state! update-in [:messages conv-id]
                   (fn [msgs]
                     (mapv #(if (= (:id %) temp-id) (-> % (assoc :status :pending) (dissoc :error-msg)) %) msgs)))
      (let [start-ms (.now js/Date)]
        (controller/dispatch :im-send-message :send
          {:conv-id    conv-id
           :content    content
           :on-success (fn [resp]
                         (let [ui-msg  (model/message->ui resp)
                               real-id (:id ui-msg)]
                           (set-conv-last-message! conv-id resp)
                           (swap-state! (fn [s]
                                          (-> s
                                              (update-in [:messages conv-id]
                                                         (fn [msgs]
                                                           (let [already? (some #(= (:id %) real-id) msgs)]
                                                             (if already?
                                                               (vec (remove #(= (:id %) temp-id) msgs))
                                                               (mapv #(if (= (:id %) temp-id) ui-msg %) msgs)))))
                                              (update :delivery-status #(if (get % real-id) % (assoc % real-id model/status-sent))))))))
           :on-error   (fn [e]
                         (if (unrecoverable-send-error? e)
                           (mark-message-failed! conv-id temp-id (get-in e [:response :body :message]))
                           (schedule-retry! conv-id temp-id content 0 start-ms)))})))))

(defn- send-message! []
  (let [conv-id (:current-conv-id @im-state)
        content (str/trim (current-input))]
    (when (and conv-id (not (str/blank? content)) (not (:sending? @im-state)))
      (swap-state! assoc :sending? true)
      (let [temp-id  (str "temp-" (.now js/Date))
            temp-msg {:id         temp-id
                      :conv-id    conv-id
                      :sender-id  (current-user-id)
                      :content    content
                      :created-at (.toISOString (js/Date.))
                      :direction  "outgoing"
                      :status     :pending}]
        (swap-state! update-in [:messages conv-id] #(conj (or % []) temp-msg))
        (swap-state! assoc-in [:input-content conv-id] "")
        (let [start-ms (.now js/Date)]
          (controller/dispatch :im-send-message :send
            {:conv-id    conv-id
             :content    content
             :on-success (fn [msg]
                           (let [ui-msg  (model/message->ui msg)
                                 real-id (:id ui-msg)]
                             (set-conv-last-message! conv-id msg)
                             (swap-state! (fn [s]
                                            (-> s
                                                (update-in [:messages conv-id]
                                                           (fn [msgs]
                                                             (let [already? (some #(= (:id %) real-id) msgs)]
                                                               (if already?
                                                                 (vec (remove #(= (:id %) temp-id) msgs))
                                                                 (mapv #(if (= (:id %) temp-id) ui-msg %) msgs)))))
                                                (update :delivery-status #(if (get % real-id) % (assoc % real-id model/status-sent)))
                                                (assoc :sending? false))))))
             :on-error   (fn [e]
                           (swap-state! assoc :sending? false)
                           (if (unrecoverable-send-error? e)
                             (mark-message-failed! conv-id temp-id (get-in e [:response :body :message]))
                             (schedule-retry! conv-id temp-id content 0 start-ms)))}))))))

;; A given user + family has exactly one direct thread, but the local list may
;; be stale — always let the backend find-or-create on (family_id, pair).
(defn- start-direct-conv! [user family-id]
  (controller/dispatch :im-create-conversation :load
    {:kind           model/kind-direct
     :participant-ids [(:id user)]
     :name           nil
     :family-id      family-id
     :on-success     (fn [conv]
                       (swap-state! update :conversations
                                    (fn [convs]
                                      (if (some #(= (:id %) (:id conv)) convs)
                                        convs
                                        (vec (cons conv convs)))))
                       (select-conversation! (:id conv)))
     :on-error       (fn [_]
                       (swap-state! assoc :error "Failed to start conversation"))}))

(defn- load-all-users-for-new-conv! []
  (swap-state! assoc :new-conv-search "" :loading-users? true)
  (-> (collection-model/permitted-groups nil)
      (p/then (fn [groups]
                (let [groups (collection-model/chat-enabled-only (or groups []))]
                  (swap-state! assoc :families groups)
                  ;; Loading stays on until every family's users arrived, so the
                  ;; picker never shows "No results" for a still-loading family.
                  (-> (p/all (map (fn [f]
                                    (-> (collection-model/permitted-users nil (:id f))
                                        (p/then (fn [users]
                                                  (swap-state! assoc-in [:users-by-family (:id f)] (or users []))))
                                        (p/catch (fn [_] nil))))
                                  groups))
                      (p/then (fn [_]
                                (swap-state! assoc :loading-users? false :users-loaded? true)))))))
      (p/catch (fn [_] (swap-state! assoc :loading-users? false)))))

(defn- ensure-users-loaded! []
  (when-not (:users-loaded? @im-state)
    (load-all-users-for-new-conv!)))

;; Families alone (without per-family users) are enough for MFO detection, the
;; chat list family filter and the conversation banner label.
(defn- load-families! []
  (-> (collection-model/permitted-groups nil)
      (p/then (fn [groups]
                (swap-state! assoc :families (collection-model/chat-enabled-only (or groups [])))
                ;; A conversation may have been selected (deep link) before the
                ;; families arrived — the MFO banner needs per-family users.
                (when (and (mfo?) (:current-conv-id @im-state))
                  (ensure-users-loaded!))))
      (p/catch (fn [_] nil))))


(defn- register-notify-handlers! []
  (when-not @handlers-registered?
    (reset! handlers-registered? true)
    
    (notify/register-handler
     "im:message_new"
     (fn [payload]
       (let [msg     (model/message->ui (:message payload))
             conv-id (:conv-id msg)
             own?    (= (:sender-id msg) (current-user-id))
             already-have? (some #(= (:id %) (:id msg))
                                 (get-in @im-state [:messages conv-id]))]
         (when-not already-have?
           (swap-state! update-in [:messages conv-id] #(conj (or % []) msg)))
         (set-conv-last-message! conv-id (:message payload))
         (when-not own?
           (controller/dispatch :im-ack-message :ack {:msg-id (:id msg)}))
         (if (and (:panel-open? @im-state)
                  (= conv-id (:current-conv-id @im-state)))
           (controller/dispatch :im-mark-read :mark {:conv-id conv-id})
           (let [known-conv (some #(when (= (:id %) conv-id) %) (:conversations @im-state))]
             (if known-conv
               (swap-state! update :conversations
                            (fn [convs]
                              (vec (cons (cond-> known-conv
                                           (not own?) (update :unread_count (fnil inc 0)))
                                         (remove #(= (:id %) conv-id) convs)))))
               (controller/dispatch :im-get-conversation :load
                 {:conv-id    conv-id
                  :on-success (fn [conv]
                                ;; Two notifications can race the same fetch —
                                ;; only the first response may add the conversation.
                                (swap-state! update :conversations
                                             (fn [convs]
                                               (if (some #(= (:id %) conv-id) convs)
                                                 convs
                                                 (vec (cons (assoc conv :unread_count (if own? 0 1)) convs))))))
                  })))))))

    (notify/register-handler
     "im:delivery_update"
     (fn [payload]
       (let [{:keys [message_id status]} payload
             status-rank {"sent" 1 "delivered" 2 "read" 3}
             new-rank    (get status-rank status 0)]
         (swap-state!
          (fn [s]
            (let [all-msgs  (:messages s)
                  conv-msgs (some (fn [[_ msgs]]
                                    (when (some #(= (:id %) message_id) msgs) msgs))
                                  all-msgs)
                  ref-ts    (when conv-msgs
                              (:created-at (some #(when (= (:id %) message_id) %) conv-msgs)))
                  to-update (if (and conv-msgs ref-ts)
                              (->> conv-msgs
                                   (filter #(and (= (:direction %) "outgoing")
                                                 (<= (compare (:created-at %) ref-ts) 0)))
                                   (map :id))
                              [message_id])]
              (reduce (fn [acc id]
                        (let [cur-rank (get status-rank (get-in acc [:delivery-status id]) 0)]
                          (if (> new-rank cur-rank)
                            (assoc-in acc [:delivery-status id] status)
                            acc)))
                      s
                      to-update)))))))

    (notify/register-handler
     "im:typing"
     (fn [payload]
       (let [{:keys [conversation_id user_id first_name last_name]} payload
             name  (str/trim (str first_name " " last_name))
             timer (js/setTimeout #(clear-typing! conversation_id user_id) 3000)]
         (when-let [old-timer (get-in @im-state [:typing conversation_id user_id :timer])]
           (js/clearTimeout old-timer))
         (swap-state! assoc-in [:typing conversation_id user_id]
                      {:name name :timer timer}))))))


(defn- user-display-name [user]
  (str/trim (str (:first_name user) " " (:last_name user))))

;; Must match the length of $chat-avatar-colors in im.scss.
(def ^:private avatar-color-count 16)

;; Deterministic person -> color mapping (placeholder until user profiles
;; carry a color): a hash of the display name picks one of the avatar
;; palette classes, so a given person always renders with the same color.
(defn- avatar-color-class [name]
  (str "c-chat-panel__avatar--c"
       (inc (mod (js/Math.abs (hash (str name))) avatar-color-count))))

(defn- initials-of [name]
  (let [initials (->> (str/split (str name) #"\s+")
                      (remove str/blank?)
                      (take 2)
                      (map #(str (first %)))
                      (apply str)
                      str/upper-case)]
    (if (str/blank? initials) "?" initials)))

(defn- conv-avatar
  ([name] (conv-avatar name nil))
  ([name size-class]
   [:div.c-chat-panel__avatar
    {:class (str (avatar-color-class name)
                 (when size-class (str " " size-class)))}
    (initials-of name)]))

;; Group conversations render as two overlapping member avatars (the first
;; two participants other than the logged-in user), like the prototype.
(defn- group-avatar [conv]
  (let [my-id  (current-user-id)
        others (->> (:participants conv)
                    (remove #(= (:user_id %) my-id))
                    (take 2))]
    [:div.c-chat-panel__avatar-stack
     (for [p others]
       ^{:key (:user_id p)}
       [conv-avatar (user-display-name p) "c-chat-panel__avatar--xs"])]))

(defn- format-time [iso-str]
  (when iso-str
    (.toLocaleTimeString (js/Date. iso-str) js/undefined #js {:hour "2-digit" :minute "2-digit"})))

(defn- date-label [iso-str]
  (when iso-str
    (let [d         (js/Date. iso-str)
          now       (js/Date.)
          d-str     (.toLocaleDateString d)
          today-str (.toLocaleDateString now)
          yest-str  (.toLocaleDateString (js/Date. (- (.getTime now) (* 24 60 60 1000))))]
      (cond
        (= d-str today-str) "Today"
        (= d-str yest-str)  "Yesterday"
        :else d-str))))

(defn- message-date [iso-str]
  (when iso-str
    (.toLocaleDateString (js/Date. iso-str))))


;; ---------------------------------------------------------------------------
;; New conversation view (panel)
;; ---------------------------------------------------------------------------

(defn- family-user-count [f]
  (let [my-id (current-user-id)]
    (count (remove #(= (:id %) my-id)
                   (get-in @im-state [:users-by-family (:id f)] [])))))

;; When the user + family already has a thread, label it with the last
;; activity ("9:42 am" today, "Yesterday", a date); "No chat yet" otherwise.
(defn- last-chat-label [user-id family-id]
  (let [conv (some (fn [c]
                     (when (and (= (:kind c) model/kind-direct)
                                (= (:family_id c) family-id)
                                (some #(= (:user_id %) user-id) (:participants c)))
                       c))
                   (:conversations @im-state))
        ts   (get-in conv [:last_message :created_at])]
    (when ts
      (let [lbl (date-label ts)]
        (if (= lbl "Today") (format-time ts) lbl)))))

(defn- new-conv-user-item [u f & [mfo-step?]]
  [:div.c-chat-panel__new-conv-item
   {:on-click #(start-direct-conv! u (:id f))}
   [conv-avatar (user-display-name u)]
   [:div.c-chat-panel__conv-content
    [:div.c-chat-panel__conv-name (user-display-name u)]
    (when-not mfo-step?
      [:div.c-chat-panel__new-conv-family-label (:name f)])]
   (if mfo-step?
     (if-let [label (last-chat-label (:id u) (:id f))]
       [:span.c-chat-panel__new-conv-time label]
       [:span.c-chat-panel__new-conv-time.c-chat-panel__new-conv-time--empty "No chat yet"])
     [:img.c-chat-panel__new-conv-chevron
      {:src "/static/img/icons/arrow-expand.svg" :alt ""}])])

;; MFO step 1: pick a family (alphabetical, with member count).
(defn- new-conv-family-step [families query]
  (let [fams (->> families
                  (filter #(or (str/blank? query)
                               (str/includes? (str/lower-case (str (:name %))) query)))
                  (sort-by #(str/lower-case (str (:name %)))))]
    [:<>
     [:div.c-chat-panel__new-conv-heading
      [:div.c-chat-panel__new-conv-heading-title "Which family is this about?"]
      [:div.c-chat-panel__new-conv-heading-hint "Pick a family, then choose who to message."]]
     (if (empty? fams)
       [:div.c-chat-panel__conv-list__empty "No results"]
       (for [f fams]
         ^{:key (:id f)}
         [:div.c-chat-panel__new-conv-item
          {:on-click #(swap-state! assoc :new-conv-family f :new-conv-search "")}
          [:div.c-chat-panel__conv-content
           [:div.c-chat-panel__conv-name (:name f)]
           [:div.c-chat-panel__new-conv-family-label
            (let [n (family-user-count f)]
              (str n (if (= n 1) " person" " people")))]]
          [:img.c-chat-panel__new-conv-chevron
           {:src "/static/img/icons/arrow-expand.svg" :alt ""}]]))]))

;; MFO step 2: pick a user of the selected family (alphabetical, role subtitle).
(defn- new-conv-user-step [f users]
  (if (empty? users)
    [:div.c-chat-panel__conv-list__empty "No results"]
    [:<>
     (for [u users]
       ^{:key (:id u)}
       [new-conv-user-item u f true])]))

;; Single-family view (pre-MFO behavior, unchanged): users grouped under
;; family name headers.
(defn- new-conv-flat-list [families users-for]
  (for [f families]
    (let [users (users-for f)]
      (when (seq users)
        ^{:key (:id f)}
        [:<>
         [:div.c-chat-panel__new-conv-section (:name f)]
         (for [u users]
           ^{:key (:id u)}
           [new-conv-user-item u f])]))))

(defn- new-conv-view []
  (let [{:keys [families users-by-family new-conv-search new-conv-family loading-users?]} @im-state
        my-id        (current-user-id)
        query        (str/lower-case (str/trim new-conv-search))
        matches?     (fn [u]
                       (or (str/blank? query)
                           (str/includes? (str/lower-case (user-display-name u)) query)))
        users-for    (fn [f]
                       (->> (get users-by-family (:id f) [])
                            (remove #(= (:id %) my-id))
                            (filter matches?)))
        mfo          (mfo?)
        family-step? (and mfo (nil? new-conv-family))
        user-step?   (and mfo new-conv-family)]
    [:<>
     [:div.c-chat-panel__header
      [:button.c-chat-panel__header-back
       {:on-click #(if user-step?
                     (swap-state! assoc :new-conv-family nil :new-conv-search "")
                     (swap-state! assoc :panel-view :list :new-conv-search "" :new-conv-family nil))}
       [:img.c-chat-panel__back-icon
        {:src "/static/img/icons/arrow-expand.svg" :alt "Back"}]]
      (if user-step?
        [:div.c-chat-panel__header-info
         [:span.c-chat-panel__header-title (:name new-conv-family)]
         [:span.c-chat-panel__header-subtitle "Choose a person"]]
        [:span.c-chat-panel__header-title (if mfo "New Message" "New Chat")])
      [:button.c-chat-panel__header-close
       {:on-click #(swap-state! assoc :panel-open? false)}
       "×"]]
     [:div.c-chat-panel__new-conv
      [:div.c-chat-panel__search
       [:div.c-chat-panel__search-wrap
        [:img.c-chat-panel__search-icon {:src "/static/img/icons/search.png" :alt ""}]
        [:input {:type        "search"
                 :placeholder (if family-step? "Search families" "Search people")
                 :value       new-conv-search
                 :auto-focus  true
                 :on-change   #(swap-state! assoc :new-conv-search (.. % -target -value))}]]]
      [:div.c-chat-panel__new-conv-list
       (cond
         loading-users?    [:div.c-chat-panel__conv-list__empty "Loading..."]
         (empty? families) [:div.c-chat-panel__conv-list__empty "No users available"]
         family-step?      (new-conv-family-step families query)
         mfo               [new-conv-user-step new-conv-family (users-for new-conv-family)]
         :else             (new-conv-flat-list families users-for))]]]))


;; ---------------------------------------------------------------------------
;; Conversation list item
;; ---------------------------------------------------------------------------

(defn- preview-for-msg [conv msg]
  (when msg
    (let [my-id     (current-user-id)
          sender-id (:sender-id msg)
          words     (str/split (str (:content msg)) #"\s+")]
      (str (when (not= sender-id my-id)
             (when-let [p (some #(when (= (:user_id %) sender-id) %) (:participants conv))]
               (str (:first_name p) ": ")))
           (str/join " " words)))))

(defn- message-preview [conv-id]
  (let [conv     (conversation-by-id conv-id)
        loaded   (last (get-in @im-state [:messages conv-id]))
        fallback (when-let [lm (:last_message conv)]
                   (model/message->ui lm))
        last-msg (or loaded fallback)]
    (preview-for-msg conv last-msg)))

(defn- conversation-item [conv]
  (let [my-id   (current-user-id)
        name    (model/conversation-display-name conv my-id)
        active? (= (:id conv) (:current-conv-id @im-state))
        preview (message-preview (:id conv))
        unread  (:unread_count conv 0)
        lm      (or (:last_message conv) {})
        ts      (format-time (:created_at lm))]
    [:div.c-chat-panel__conv-item
     {:class    (str (when active? "is-active") (when (and (pos? unread) (not active?)) " is-unread"))
      :on-click #(select-conversation! (:id conv))}
     (if (= (:kind conv) model/kind-group)
       [group-avatar conv]
       [conv-avatar name])
     [:div.c-chat-panel__conv-content
      [:div.c-chat-panel__conv-row
       [:span.c-chat-panel__conv-name name]
       (when ts [:span.c-chat-panel__conv-time ts])]
      (when (mfo?)
        [:div.c-chat-panel__conv-family (:family_name conv)])
      [:div.c-chat-panel__conv-bottom
       [:span.c-chat-panel__conv-preview preview]
       (when (and (pos? unread) (not active?))
         [:span.c-chat-panel__conv-badge unread])]]]))


;; ---------------------------------------------------------------------------
;; Conversation list view
;; ---------------------------------------------------------------------------

(defn- family-filter-dropdown []
  (let [{:keys [families family-filter]} @im-state]
    [:select.c-chat-panel__family-filter
     {:value     (str (or family-filter ""))
      :on-change (fn [e]
                   (let [v (.. e -target -value)]
                     (swap-state! assoc :family-filter
                                  (when-not (str/blank? v) (js/parseInt v 10)))))}
     [:option {:value ""} "All Families"]
     (for [f (sort-by #(str/lower-case (str (:name %))) families)]
       ^{:key (:id f)}
       [:option {:value (str (:id f))} (:name f)])]))

(defn- panel-list-view []
  (let [{:keys [active-tab search-query conversations family-filter]} @im-state
        query     (str/lower-case (str/trim (str search-query)))
        my-id     (current-user-id)
        mfo       (mfo?)
        filtered  (cond->> conversations
                    (= active-tab :unread) (filter #(pos? (:unread_count % 0)))
                    (and mfo family-filter) (filter #(= (:family_id %) family-filter))
                    (not (str/blank? query)) (filter #(or (str/includes?
                                                           (str/lower-case (model/conversation-display-name % my-id))
                                                           query)
                                                          (str/includes?
                                                           (str/lower-case (str (:family_name %)))
                                                           query))))
        unread-total (total-unread)]
    [:<>
     [:div.c-chat-panel__header
      [:span.c-chat-panel__header-title "Messages"]
      [:button.c-chat-panel__new-btn
       {:on-click #(do (ensure-users-loaded!)
                       (swap-state! assoc :panel-view :new-conv :new-conv-family nil
                                    :new-conv-search ""))}
       [:svg {:width "13" :height "13" :view-box "0 0 24 24" :fill "none"}
        [:path {:d "M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
                :stroke "currentColor" :stroke-width "2.5" :stroke-linecap "round" :stroke-linejoin "round"}]]
       "New"]
      [:button.c-chat-panel__header-close
       {:on-click #(swap-state! assoc :panel-open? false)}
       "×"]]

     [:div.c-chat-panel__search
      [:div.c-chat-panel__search-wrap
       [:img.c-chat-panel__search-icon {:src "/static/img/icons/search.png" :alt ""}]
       [:input {:type        "search"
                :placeholder (if mfo
                               "Search people, families, and messages"
                               "Search people and messages")
                :value       search-query
                :on-change   #(swap-state! assoc :search-query (.. % -target -value))}]]]

     [:div.c-chat-panel__tabs
      {:class (when mfo "c-chat-panel__tabs--with-filter")}
      [:div.c-chat-panel__tab
       {:class    (when (= active-tab :all) "is-active")
        :on-click #(swap-state! assoc :active-tab :all)}
       "All"]
      [:div.c-chat-panel__tab
       {:class    (when (= active-tab :unread) "is-active")
        :on-click #(swap-state! assoc :active-tab :unread)}
       "Unread"
       (when (pos? unread-total)
         [:span.c-chat-panel__tab-badge unread-total])]
      (when mfo
        [family-filter-dropdown])]

     [:div.c-chat-panel__conv-list
      (if (empty? filtered)
        [:div.c-chat-panel__conv-list__empty
         (if (str/blank? query) "No conversations" "No results")]
        (for [conv filtered]
          ^{:key (:id conv)}
          [conversation-item conv]))]]))


;; ---------------------------------------------------------------------------
;; Messages view
;; ---------------------------------------------------------------------------


(defn- sender-name [msg]
  (let [conv (conversation-by-id (:conv-id msg))]
    (->> (:participants conv)
         (filter #(= (:user_id %) (:sender-id msg)))
         first
         user-display-name)))

(defn- message-item [msg]
  (let [incoming?       (= (:direction msg) "incoming")
        status          (:status msg)
        failed?         (= :failed status)
        pending?        (= :pending status)
        conv-id         (:conv-id msg)
        ts              (format-time (:created-at msg))
        delivery-status (get-in @im-state [:delivery-status (:id msg)])
        retry-count     (get-in @im-state [:retrying (:id msg) :count])
        error-msg       (:error-msg msg)]
    [:div.c-chat-panel__message
     {:class (str "c-chat-panel__message--" (:direction msg)
                  (when failed? " c-chat-panel__message--failed"))}
     [:div.c-chat-panel__msg-wrap
      (if incoming?
        [:div.c-chat-panel__msg-sender-line (sender-name msg)]
        [:div.c-chat-panel__msg-status
         {:class (when failed? "is-failed")}
         (cond
           (and failed? error-msg) error-msg
           failed?  "Message failed to send"
           pending? [:<>
                     [:span.c-chat-panel__msg-spinner]
                     (when (and retry-count (pos? retry-count))
                       [:span.c-chat-panel__msg-retrying (str " Retrying... (" retry-count ")")])]
           :else    (str ts " • " (str/capitalize (or delivery-status model/status-sent))))])
      [:div.c-chat-panel__msg-body-row
       (when incoming?
         [conv-avatar (sender-name msg) "c-chat-panel__avatar--sm"])
       [:div.c-chat-panel__bubble
        [:div (:content msg)]]
       (when (and failed? (not error-msg))
         [:a.c-chat-panel__msg-retry-btn
          {:href     "#"
           :on-click (fn [e] (.preventDefault e) (retry-message! conv-id msg))}
          [:svg {:width "16" :height "16" :view-box "0 0 24 24" :fill "none"}
           [:path {:d "M14 15L10 19L14 23" :stroke "currentColor" :stroke-width "2"}]
           [:path {:d "M5.93782 15.5C5.16735 14.1655 4.85875 12.6141 5.05989 11.0863C5.26102 9.55856 5.96064 8.13986 7.05025 7.05025C8.13986 5.96064 9.55856 5.26102 11.0863 5.05989C12.6141 4.85875 14.1655 5.16735 15.5 5.93782C16.8345 6.70829 17.8775 7.89757 18.4672 9.32122C19.0568 10.7449 19.1603 12.3233 18.7615 13.8117C18.3627 15.3002 17.4838 16.6154 16.2613 17.5535C15.0388 18.4915 13.5409 19 12 19"
                  :stroke "currentColor" :stroke-width "2" :stroke-linecap "round"}]]])]
      (when incoming?
        [:div.c-chat-panel__msg-time ts])]]))

;; Form-2 component: the closure must be created once per mounted instance —
;; calling this inside another render fn would remount the list (and reset the
;; scroll position) on every parent re-render.
(defn- messages-list [_conv-id]
  (let [!node    (volatile! nil)
        !last-id (volatile! nil)]
    (fn [conv-id]
      (let [messages  (conv-messages conv-id)
            loading?  (:loading-messages? @im-state)
            has-more? (get-in @im-state [:has-more-messages? conv-id])
            new-id    (:id (last messages))]
        (when (not= new-id @!last-id)
          (vreset! !last-id new-id)
          (reagent/after-render
           #(when-let [node @!node]
              (set! (.-scrollTop node) (.-scrollHeight node)))))
        [:div.c-chat-panel__messages
         {:ref #(vreset! !node %)}
         (when has-more?
           [:div.c-chat-panel__messages__load-more
            [:button {:on-click #(when-let [first-id (:id (first messages))]
                                   (load-messages! conv-id first-id))
                      :disabled loading?}
             (if loading? "Loading..." "Load earlier messages")]])
         (if (empty? messages)
           (when-not loading?
             [:div.c-chat-panel__messages__empty "No messages yet. Say something!"])
           (let [grouped (partition-by #(message-date (:created-at %)) messages)]
             (for [group grouped]
               (let [date-str (date-label (:created-at (first group)))]
                 ^{:key (str "grp-" date-str "-" (:id (first group)))}
                 [:<>
                  [:div.c-chat-panel__date-sep date-str]
                  (for [msg group]
                    ^{:key (:id msg)}
                    [message-item msg])]))))]))))

(defn- conv-first-other-name [conv-id]
  (let [conv  (conversation-by-id conv-id)
        my-id (current-user-id)]
    (->> (:participants conv)
         (remove #(= (:user_id %) my-id))
         first
         user-display-name)))

;; Input placeholder target: the peer for direct chats, the whole group for
;; group chats.
(defn- conv-input-target-name [conv-id]
  (let [conv (conversation-by-id conv-id)]
    (if (= (:kind conv) model/kind-group)
      (model/conversation-display-name conv (current-user-id))
      (conv-first-other-name conv-id))))

(defn- typing-indicator [conv-id]
  (let [typers (vals (get-in @im-state [:typing conv-id]))
        text   (when (seq typers)
                 (let [names (map :name typers)]
                   (str (str/join ", " names)
                        (if (= 1 (count names)) " is typing..." " are typing..."))))]
    [:div.c-chat-panel__typing text]))

;; Composition box auto-grows with content up to half the viewport height,
;; then becomes scrollable. Reserves the send-button gutter via right padding
;; so text never slides under the button (see #2889).
(def ^:private input-max-height-ratio 0.5)

;; Below this width the input is clearly inside the collapsed / still
;; sliding-open panel (full panel width is 400px), not laid out for real.
(def ^:private input-measurable-min-width 200)

(defn- autosize-input! [el]
  (when (and el (.-isConnected el))
    (if (< (.-offsetWidth el) input-measurable-min-width)
      ;; The panel is collapsed or mid slide-open (width animates 0 -> 400px):
      ;; measuring now would wrap the text in a too-narrow box and blow the
      ;; height up. Retry once the 0.3s transition is over; the isConnected
      ;; guard stops the retry when the panel content unmounts meanwhile.
      (js/setTimeout #(autosize-input! el) 350)
      (do
        ;; reset to measure the natural content height
        (set! (.. el -style -height) "auto")
        (let [max-h    (* input-max-height-ratio (.-innerHeight js/window))
              scroll-h (.-scrollHeight el)
              h        (min scroll-h max-h)]
          (set! (.. el -style -height) (str h "px"))
          (set! (.. el -style -overflowY) (if (> scroll-h max-h) "auto" "hidden")))))))

(defn- input-can-send? [conv-id]
  (and (not (:sending? @im-state))
       (not (str/blank? (get-in @im-state [:input-content conv-id] "")))))

(defn- chat-input [_conv-id]
  (let [el-ref (atom nil)]
    (reagent/create-class
     {:display-name "chat-input"
      :component-did-mount  (fn [_] (autosize-input! @el-ref))
      :component-did-update (fn [_ _] (autosize-input! @el-ref))
      :reagent-render
      (fn [conv-id]
        (let [input (get-in @im-state [:input-content conv-id] "")]
          [:textarea.c-chat-panel__input
           {:ref         (fn [el] (reset! el-ref el))
            :rows        1
            :placeholder (str "Message to " (conv-input-target-name conv-id))
            :value       input
            :on-change   (fn [e]
                           (swap-state! assoc-in [:input-content conv-id]
                                        (.. e -target -value))
                           (autosize-input! (.-target e))
                           (send-typing! conv-id))
            :on-key-down (fn [e]
                           (when (and (= (.-key e) "Enter") (not (.-shiftKey e)))
                             (.preventDefault e)
                             (when (input-can-send? conv-id) (send-message!))))}]))})))

(defn- conv-peer [conv]
  (let [my-id (current-user-id)]
    (->> (:participants conv)
         (remove #(= (:user_id %) my-id))
         first)))

(defn- common-families-with [user-id]
  (let [{:keys [families users-by-family]} @im-state]
    (filter (fn [f]
              (some #(= (:id %) user-id) (get users-by-family (:id f))))
            families)))

;; MFO banner pinned above the messages: the thread's family on the left;
;; when the peer shares more than one family with the logged-in user, a
;; "Change" control on the right switches to (or creates) the thread for
;; that user + family.
(defn- conv-family-banner [conv]
  (let [peer   (when (= (:kind conv) model/kind-direct) (conv-peer conv))
        common (when peer (common-families-with (:user_id peer)))]
    [:div.c-chat-panel__family-banner
     [:span.c-chat-panel__family-banner-name (:family_name conv)]
     (when (> (count common) 1)
       [:select.c-chat-panel__family-banner-change
        {:value     ""
         :on-change (fn [e]
                      (let [v (.. e -target -value)]
                        (when-not (str/blank? v)
                          (start-direct-conv! {:id (:user_id peer)} (js/parseInt v 10)))))}
        [:option {:value "" :disabled true} "Change"]
        (for [f (->> common
                     (remove #(= (:id %) (:family_id conv)))
                     (sort-by #(str/lower-case (str (:name %)))))]
          ^{:key (:id f)}
          [:option {:value (str (:id f))} (:name f)])])]))

(defn- panel-conv-view [conv-id]
  (let [my-id  (current-user-id)
        conv   (conversation-by-id conv-id)
        title  (if conv (model/conversation-display-name conv my-id) "")
        mfo    (mfo?)
        group? (= (:kind conv) model/kind-group)
        can-send? (input-can-send? conv-id)]
    [:<>
     [:div.c-chat-panel__header
      [:button.c-chat-panel__header-back
       {:on-click #(swap-state! assoc :panel-view :list :current-conv-id nil)}
       [:img.c-chat-panel__back-icon
        {:src "/static/img/icons/arrow-expand.svg" :alt "Back"}]]
      (if (or mfo group?)
        [:<>
         (when conv
           (if group? [group-avatar conv] [conv-avatar title]))
         [:div.c-chat-panel__header-info
          [:span.c-chat-panel__header-title title]
          (when group?
            [:span.c-chat-panel__header-subtitle
             (str (count (:participants conv)) " members")])]]
        [:span.c-chat-panel__header-title title])
      [:button.c-chat-panel__header-close
       {:on-click #(swap-state! assoc :panel-open? false)}
       "×"]]
     (when (and mfo conv)
       [conv-family-banner conv])
     [messages-list conv-id]
     [typing-indicator conv-id]
     [:div.c-chat-panel__input-bar
      [:div.c-chat-panel__input-wrap
       [chat-input conv-id]
       [:button.c-chat-panel__send-btn
        {:disabled (not can-send?)
         :on-click #(send-message!)}
        [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24"
               :width "16" :height "16" :fill "white"}
         [:path {:d "M12 4l-8 8h5v8h6v-8h5z"}]]]]]]))


;; ---------------------------------------------------------------------------
;; Public components
;; ---------------------------------------------------------------------------

(defn- toggle-panel! []
  (let [will-open? (not (:panel-open? @im-state))
        conv-id    (:current-conv-id @im-state)]
    (swap-state! update :panel-open? not)
    (when (and will-open? conv-id)
      (swap-state! update :conversations
                   (fn [convs]
                     (mapv #(if (= (:id %) conv-id) (assoc % :unread_count 0) %) convs)))
      (controller/dispatch :im-mark-read :mark {:conv-id conv-id}))))

(defn chat-icon []
  (fn []
    (let [unread (total-unread)]
      [:span.c-chat-icon
       {:on-click toggle-panel!}
       [:img.svg-icon--s {:src "/static/img/icons/im-white.svg" :alt "Chat"}]
       (when (pos? unread)
         [:span.c-chat-icon__badge unread])])))

(defn- set-outer-container-class! [panel-open?]
  (when-let [el (.querySelector js/document "div.outer-container")]
    (if panel-open?
      (.add (.-classList el) "has-chat-panel")
      (.remove (.-classList el) "has-chat-panel"))))

(defn chat-panel []
  (reagent/create-class
   {:component-did-mount
    (fn [_]
      (load-conversations!)
      (load-families!)
      (register-notify-handlers!))
    :component-will-unmount
    (fn [_]
      (set-outer-container-class! false)
      (reset! handlers-registered? false)
      (cancel-all-retries!)
      (notify/register-handler "im:message_new" nil)
      (notify/register-handler "im:delivery_update" nil)
      (notify/register-handler "im:typing" nil))
    :reagent-render
    (fn []
      (let [{:keys [panel-open? panel-view current-conv-id]} @im-state]
        (set-outer-container-class! panel-open?)
        [:div.c-chat-panel
         {:class (when panel-open? "is-open")}
         (when panel-open?
           (case panel-view
             :list         [panel-list-view]
             :conversation (when current-conv-id [panel-conv-view current-conv-id])
             :new-conv     [new-conv-view]
             [panel-list-view]))]))}))


;; ---------------------------------------------------------------------------
;; Route entry points for #/im
;; ---------------------------------------------------------------------------

(defn open-panel! [& [conv-id]]
  (if conv-id
    (do
      (select-conversation! conv-id)
      (swap-state! assoc :panel-open? true))
    (swap-state! assoc :panel-open? true :panel-view :list)))
